import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function RainyDayVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "rainy-day-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Vancouver Aquarium",
    "section-2": "Science World",
    "section-3": "Granville Island Public Market",
    "section-4": "Bloedel Conservatory",
    "section-5": "Vancouver Art Gallery",
    "section-6": "FlyOver Canada",
    "section-7": "Luxury Spa Retreat",
    faq: "FAQ",
    "local-regulations": "Local Regulations",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.destinationvancouver.com",
      label: "Destination Vancouver – Rainy Day Adventures",
    },
    {
      url: "https://granvilleisland.com/public-market",
      label: "Granville Island Public Market (Official Site)",
    },
    {
      url: "https://www.scienceworld.ca",
      label: "TELUS Science World Official",
    },
    {
      text: "Various information sourced from the conversation details (2025).",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Each location references a specific section
  const locations = [
    {
      id: "section-1",
      lat: 49.30080,
      lng: -123.13093,
      popupText: "Vancouver Aquarium, Stanley Park",
    },
    {
      id: "section-2",
      lat: 49.27325,
      lng: -123.10377,
      popupText: "Science World at TELUS World of Science",
    },
    {
      id: "section-3",
      lat: 49.27262,
      lng: -123.13524,
      popupText: "Granville Island Public Market",
    },
    {
      id: "section-4",
      lat: 49.24159,
      lng: -123.11348,
      popupText: "Bloedel Conservatory in Queen Elizabeth Park",
    },
    {
      id: "section-5",
      lat: 49.28313,
      lng: -123.12047,
      popupText: "Vancouver Art Gallery",
    },
    {
      id: "section-6",
      lat: 49.28880,
      lng: -123.11125,
      popupText: "FlyOver Canada at Canada Place",
    },
    {
      id: "section-7",
      lat: 49.28828,
      lng: -123.11709,
      popupText: "Fairmont Pacific Rim’s Willow Stream Spa",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Things to Do in Vancouver on a Rainy Day</title>
        <meta
          name="description"
          content="Discover the best indoor activities in Vancouver when it's raining, including the aquarium, science museums, markets, and more."
        />
        <meta
          name="keywords"
          content="Vancouver, rainy day, indoor activities, travel tips"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/rainy-day-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Things to Do in Vancouver on a Rainy Day"
        />
        <meta
          property="og:description"
          content="Explore Vancouver's best indoor attractions, from cultural venues to family-friendly experiences, perfect for when the rain won't quit."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/rainy-day-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/rainy-day-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Things to Do in Vancouver on a Rainy Day"
        />
        <meta
          name="twitter:description"
          content="Explore indoor attractions like the Vancouver Aquarium, Science World, and more."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/rainy-day-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Visitors enjoying a covered Vancouver skyline in the rain"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver is famous for its natural beauty—and its rainy weather.
              While the city sees many drizzly days, there are still plenty of
              ways to have fun and stay dry. From immersive museums to tropical
              conservatories, Vancouver’s indoor attractions ensure you can
              explore, learn, and indulge without letting the rain dampen your
              spirits.
            </p>

            {/* SECTION 1: Vancouver Aquarium */}
            <h2 id="section-1">Vancouver Aquarium</h2>
            <p ref={(el) => setSpotRef("section-1", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Vancouver Aquarium (Latitude: 49.30080, Longitude: -123.13093)</strong>
              <Marker
                coordY={49.30080}
                coordX={-123.13093}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              Located in Stanley Park, the Vancouver Aquarium is Canada’s largest
              and offers a perfect rainy-day escape. With over 65,000 animals
              across 120 interactive exhibits, visitors can see rescued sea
              otters, penguins, and vibrant jellyfish displays. A 4D theater
              provides an immersive experience complete with mist, scents, and
              wind effects.
            </p>
            <ul>
              <li>
                <strong>Address:</strong> 845 Avison Way, Vancouver, BC V6G 3E2
              </li>
              <li>
                <strong>Hours:</strong> Generally 9:30 AM – 5:30 PM, check
                schedules
              </li>
              <li>
                <strong>Approx. Cost:</strong> CA$49 for adults (discounts
                available)
              </li>
            </ul>
            <p>
              Insider tip: Arrive early to avoid crowds. Purchase tickets online
              to skip the line, and be sure to watch the feeding sessions for an
              educational treat.
            </p>

            {/* SECTION 2: Science World */}
            <h2 id="section-2">Science World</h2>
            <p ref={(el) => setSpotRef("section-2", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Science World at TELUS World of Science (Latitude: 49.27325, Longitude: -123.10377)</strong>
              <Marker
                coordY={49.27325}
                coordX={-123.10377}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              The geodesic dome along False Creek houses interactive exhibits on
              physics, biology, and technology. Kids and adults alike will find
              plenty of hands-on activities, a live science show, and an OMNIMAX
              theatre for breathtaking educational films. Plan to spend two to
              three hours exploring.
            </p>
            <ul>
              <li>
                <strong>Address:</strong> 1455 Quebec Street, Vancouver, BC V6A
                3Z7
              </li>
              <li>
                <strong>Hours:</strong> Typically 10 AM – 5 PM daily
              </li>
              <li>
                <strong>Approx. Cost:</strong> CA$34.85 for adults; discounts for
                youth and seniors
              </li>
            </ul>
            <p>
              Expect bigger crowds on weekends or school holidays. Buying tickets
              in advance can help you avoid long lines.
            </p>

            {/* SECTION 3: Granville Island Public Market */}
            <h2 id="section-3">Granville Island Public Market</h2>
            <p ref={(el) => setSpotRef("section-3", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Granville Island Public Market (Latitude: 49.27262, Longitude: -123.13524)</strong>
              <Marker
                coordY={49.27262}
                coordX={-123.13524}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              Foodies will delight in the sights, smells, and tastes of Granville
              Island’s indoor market. Sample fresh produce, local cheese, baked
              goods, and artisanal products. The Kids Market and nearby art
              studios also make this spot a cultural hub for all ages. It’s free
              to explore, and you only pay for what you choose to eat or buy.
            </p>
            <ul>
              <li>
                <strong>Address:</strong> 1669 Johnston Street, Vancouver, BC V6H
                3R9
              </li>
              <li>
                <strong>Hours:</strong> Open daily, 9 AM – 6 PM
              </li>
              <li>
                <strong>Approx. Cost:</strong> Free to enter; pay-as-you-go for
                food and crafts
              </li>
            </ul>
            <p>
              Try the fresh donuts from Lee’s Donuts or warm up with coffee at JJ
              Bean. It can get busy on weekends, so consider visiting on a
              weekday if you want a more relaxed vibe.
            </p>

            {/* SECTION 4: Bloedel Conservatory */}
            <h2 id="section-4">Bloedel Conservatory</h2>
            <p ref={(el) => setSpotRef("section-4", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Bloedel Conservatory (Latitude: 49.24159, Longitude: -123.11348)</strong>
              <Marker
                coordY={49.24159}
                coordX={-123.11348}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              Escape the drizzle by stepping into this tropical oasis in Queen
              Elizabeth Park. The domed greenhouse features over 500 exotic
              plants and flowers, plus more than 100 free-flying birds. The
              environment is warm and humid, offering a quick getaway from the
              cold rain outside.
            </p>
            <ul>
              <li>
                <strong>Address:</strong> 4600 Cambie Street, Vancouver, BC V5Y
                2M4
              </li>
              <li>
                <strong>Hours:</strong> Vary by season; generally 10 AM – 4 PM in
                winter
              </li>
              <li>
                <strong>Approx. Cost:</strong> CA$9 for adults; discounts for
                youth and seniors
              </li>
            </ul>
            <p>
              Plan to spend about an hour here. It’s also a prime spot for nature
              photography—just remember your lens may fog due to humidity.
            </p>

            {/* SECTION 5: Vancouver Art Gallery */}
            <h2 id="section-5">Vancouver Art Gallery</h2>
            <p ref={(el) => setSpotRef("section-5", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Vancouver Art Gallery (Latitude: 49.28313, Longitude: -123.12047)</strong>
              <Marker
                coordY={49.28313}
                coordX={-123.12047}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              One of Canada’s premier art museums, the Vancouver Art Gallery
              boasts extensive collections, including works by Emily Carr. Housed
              in a historic courthouse building, it offers four floors of
              artistic exploration. Guided tours may be included with admission,
              so check the schedule.
            </p>
            <ul>
              <li>
                <strong>Address:</strong> 750 Hornby Street, Vancouver, BC V6Z
                2H7
              </li>
              <li>
                <strong>Hours:</strong> 10 AM – 5 PM most days; closed Tuesdays
              </li>
              <li>
                <strong>Approx. Cost:</strong> CA$35 for adults; youth 13–18 are
                free
              </li>
            </ul>
            <p>
              The building itself is a work of art. Don’t forget to check for
              free or “by donation” evenings, often offered once a month.
            </p>

            {/* SECTION 6: FlyOver Canada */}
            <h2 id="section-6">FlyOver Canada</h2>
            <p ref={(el) => setSpotRef("section-6", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>FlyOver Canada (Latitude: 49.28880, Longitude: -123.11125)</strong>
              <Marker
                coordY={49.28880}
                coordX={-123.11125}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              Located at Canada Place, FlyOver Canada is a flight simulation ride
              offering a thrilling virtual journey across the nation’s most
              stunning landscapes. You’ll sit in suspended seats facing a large
              spherical screen with wind and mist effects to enhance the
              experience.
            </p>
            <ul>
              <li>
                <strong>Address:</strong> 999 Canada Place, Vancouver, BC V6C 3E1
              </li>
              <li>
                <strong>Hours:</strong> Typically 10 AM – 7 PM or later on
                weekends
              </li>
              <li>
                <strong>Approx. Cost:</strong> CA$30–$40 for adults; children’s
                tickets slightly cheaper
              </li>
            </ul>
            <p>
              Pre-booking recommended. Minimum height requirement is 102 cm (40")
              for children, and ride durations start every 15–20 minutes.
            </p>

            {/* SECTION 7: Luxury Spa Retreat */}
            <h2 id="section-7">Luxury Spa Retreat</h2>
            <p ref={(el) => setSpotRef("section-7", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Fairmont Pacific Rim’s Willow Stream Spa (Latitude: 49.28828, Longitude: -123.11709)</strong>
              <Marker
                coordY={49.28828}
                coordX={-123.11709}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              For a day of pampering, head to the Willow Stream Spa at the
              Fairmont Pacific Rim or one of Vancouver’s other luxury spas.
              Treatments can include massage rituals inspired by the rainforest
              or hot stone therapies for deep relaxation. Enjoy the covered
              whirlpool while rain falls beyond the terrace.
            </p>
            <ul>
              <li>
                <strong>Address:</strong> 1038 Canada Place, Vancouver, BC V6C
                0B9
              </li>
              <li>
                <strong>Hours:</strong> Often 8 AM – 10 PM daily; call ahead to
                confirm
              </li>
              <li>
                <strong>Approx. Cost:</strong> CA$150–$250+ for treatments
              </li>
            </ul>
            <p>
              If a full spa service is out of budget, some offer day passes for
              limited access to the amenities. It’s a perfect retreat from the
              gloomy weather.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Do places close because of rain?</h3>
              <p>
                Most attractions remain open regardless of rain. In fact, indoor
                venues may be more crowded on rainy days. Always check official
                websites or call ahead for special holiday closures or extreme
                weather updates.
              </p>

              <h3>2. What should I pack for rainy Vancouver?</h3>
              <p>
                A waterproof jacket, compact umbrella, and comfortable footwear
                are must-haves. Dress in layers so you can adapt to indoor
                heating and outdoor chill.
              </p>

              <h3>3. Are there any free indoor activities?</h3>
              <p>
                Yes! Granville Island Public Market is free to enter, and
                libraries or city-run art galleries may have free admission days.
                Keep an eye out for “by donation” hours at the Vancouver Art
                Gallery and other sites.
              </p>
            </div>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations</h2>
            <p>
              Vancouver enforces no-smoking policies within indoor public spaces
              and near building entrances. While most indoor venues welcome
              children, check height restrictions (e.g., FlyOver Canada) and
              supervise kids for safety. If you explore parks in the rain, obey
              any trail closure signs, as heavy rain can cause slippery or
              flooded paths.
            </p>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Despite the city’s reputation for drizzly weather, there’s no
              shortage of fun and cozy indoor adventures in Vancouver. Whether
              you’re sampling local delicacies at Granville Island, gazing at
              marine life, or enjoying a spa day, a rainy forecast can add an
              atmospheric charm to your trip. Embrace the rain, and discover all
              the unique ways Vancouver shines—no matter the weather.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay ref={mobileMapRef} markers={locations} spotRefs={spotRefs} />

      <Footer />
    </>
  );
}